import { useEffect, useMemo, useState, type MutableRefObject } from "react";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import { apiFetch, apiFetchBlob } from "@/lib/api";
import {
  defaultWirelessReportDateRange,
  formatWirelessCell,
  WirelessDeviceLink,
} from "./wirelessUiShared";

type DeviceOption = { deviceId: number; imei: string; hubSerial: string; realName: string };

type ReportKind = "checkins" | "events" | "currents";

const REPORT_META: Record<
  ReportKind,
  { apiPath: string; exportPath: string; csvFileName: string; title: string; columnLayoutTableId: string }
> = {
  checkins: {
    apiPath: "/api/wireless/checkins",
    exportPath: "/api/wireless/checkins/export",
    csvFileName: "wireless-checkins.csv",
    title: "Check-ins",
    columnLayoutTableId: "wireless-checkins",
  },
  events: {
    apiPath: "/api/wireless/events",
    exportPath: "/api/wireless/events/export",
    csvFileName: "wireless-events.csv",
    title: "Events",
    columnLayoutTableId: "wireless-events",
  },
  currents: {
    apiPath: "/api/wireless/currents",
    exportPath: "/api/wireless/currents/export",
    csvFileName: "wireless-currents.csv",
    title: "Hourly currents",
    columnLayoutTableId: "wireless-currents",
  },
};

type ReportRow = Record<string, string | number | null> & { deviceId?: number | null };

const PAGE_SIZE = 50;

function deviceLabel(device: DeviceOption): string {
  return [device.realName, device.hubSerial, device.imei].filter((part) => part.trim() !== "").join(" · ");
}

function buildReportColumns(columnIds: readonly string[]): HubDataTableColumn<ReportRow>[] {
  return columnIds.map((columnId) => ({
    id: columnId,
    header: columnId,
    overflow: "nowrap" as const,
    sortValue: (row) => {
      const value = row[columnId];
      if (value == null) return "";
      return value;
    },
    cell: (row) => {
      if (columnId === "IMEINumber") {
        return (
          <WirelessDeviceLink
            deviceId={row.deviceId ?? null}
            label={formatWirelessCell(row[columnId]) || formatWirelessCell(row.IMEINumber)}
          />
        );
      }
      return formatWirelessCell(row[columnId]);
    },
  }));
}

export function WirelessReportDataPanel({
  kind,
  fixedDeviceId,
  organizationId,
  groupIdsParam = "",
  downloadCsvRef,
  onDownloadDisabledChange,
}: {
  kind: ReportKind;
  fixedDeviceId?: number;
  organizationId?: number;
  groupIdsParam?: string;
  downloadCsvRef?: MutableRefObject<(() => Promise<void>) | null>;
  onDownloadDisabledChange?: (disabled: boolean) => void;
}) {
  const defaults = useMemo(() => defaultWirelessReportDateRange(), []);
  const [fromDate, setFromDate] = useState(defaults.fromDate);
  const [toDate, setToDate] = useState(defaults.toDate);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [applied, setApplied] = useState({
    fromDate: defaults.fromDate,
    toDate: defaults.toDate,
    deviceIds: fixedDeviceId != null ? String(fixedDeviceId) : "",
    page: 0,
    organizationId: organizationId ?? null,
    groupIds: groupIdsParam,
  });

  useEffect(() => {
    setApplied((current) => ({
      ...current,
      organizationId: organizationId ?? null,
      groupIds: groupIdsParam,
    }));
  }, [organizationId, groupIdsParam]);

  const devicesQuery = useQuery({
    queryKey: ["wireless", "checkins", "devices"],
    queryFn: () => apiFetch<{ devices: DeviceOption[] }>("/api/wireless/checkins/devices"),
    enabled: fixedDeviceId == null,
  });

  const deviceOptions = useMemo(() => {
    const devices = devicesQuery.data?.devices ?? [];
    return devices.map((device) => ({
      value: String(device.deviceId),
      label: deviceLabel(device),
    }));
  }, [devicesQuery.data?.devices]);

  const { apiPath, exportPath, csvFileName, title, columnLayoutTableId } = REPORT_META[kind];

  const dataQuery = useQuery({
    queryKey: ["wireless", kind, applied, fixedDeviceId, organizationId],
    queryFn: () => {
      const params = new URLSearchParams({
        fromDate: applied.fromDate,
        toDate: applied.toDate,
        page: String(applied.page),
        pageSize: String(PAGE_SIZE),
      });
      if (applied.deviceIds.trim() !== "") {
        params.set("deviceIds", applied.deviceIds);
      }
      if (applied.organizationId != null) {
        params.set("organizationId", String(applied.organizationId));
      }
      if (applied.groupIds.trim() !== "") {
        params.set("groupIds", applied.groupIds);
      }
      return apiFetch<{
        columns: string[];
        rows: ReportRow[];
        total: number;
      }>(`${apiPath}?${params.toString()}`);
    },
  });

  const columns = useMemo(
    () => buildReportColumns(dataQuery.data?.columns ?? []),
    [dataQuery.data?.columns],
  );
  const rows = dataQuery.data?.rows ?? [];
  const total = dataQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);

  function applyFilters(nextPage = 0) {
    const deviceIds =
      fixedDeviceId != null
        ? String(fixedDeviceId)
        : [...selectedDeviceIds].sort((left, right) => Number(left) - Number(right)).join(",");
    setApplied({
      fromDate,
      toDate,
      deviceIds,
      page: nextPage,
      organizationId: organizationId ?? null,
      groupIds: groupIdsParam,
    });
    setPage(nextPage);
  }

  async function downloadCsv() {
    const params = new URLSearchParams({ fromDate: applied.fromDate, toDate: applied.toDate });
    if (applied.deviceIds.trim() !== "") {
      params.set("deviceIds", applied.deviceIds);
    }
    if (applied.organizationId != null) {
      params.set("organizationId", String(applied.organizationId));
    }
    if (applied.groupIds.trim() !== "") {
      params.set("groupIds", applied.groupIds);
    }
    const { blob } = await apiFetchBlob(`${exportPath}?${params.toString()}`);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = csvFileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const downloadDisabled = dataQuery.isLoading || dataQuery.isError;
  const downloadInPageHeader = downloadCsvRef != null;
  if (downloadCsvRef != null) {
    downloadCsvRef.current = downloadCsv;
  }

  useEffect(() => {
    return () => {
      if (downloadCsvRef != null) {
        downloadCsvRef.current = null;
      }
    };
  }, [downloadCsvRef]);

  useEffect(() => {
    onDownloadDisabledChange?.(downloadDisabled);
  }, [downloadDisabled, onDownloadDisabledChange]);

  return (
    <div className="space-y-4">
      {downloadInPageHeader ? null : (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" onClick={() => void downloadCsv()} disabled={downloadDisabled}>
            Download CSV
          </Button>
        </div>
      )}

      <Card>
        <CardBody className="grid gap-4 py-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-600" htmlFor={`${kind}-from`}>
              From date
            </label>
            <Input
              id={`${kind}-from`}
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-600" htmlFor={`${kind}-to`}>
              To date
            </label>
            <Input
              id={`${kind}-to`}
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="mt-1"
            />
          </div>
          {fixedDeviceId == null && organizationId == null ? (
            <div className="md:col-span-2">
              <span className="block text-xs font-medium uppercase tracking-wide text-slate-600">Devices</span>
              <SearchableMultiSelect
                className="mt-1"
                selectedValues={selectedDeviceIds}
                onSelectedValuesChange={setSelectedDeviceIds}
                options={deviceOptions}
                searchValue={deviceSearch}
                onSearchChange={setDeviceSearch}
                placeholder="All devices"
                emptySelectionMeansAll
                isLoading={devicesQuery.isLoading}
                loadingMessage="Loading devices..."
                emptyMessage="No devices match this search."
              />
            </div>
          ) : null}
          <div className="flex items-end md:col-span-2 lg:col-span-4">
            <Button type="button" onClick={() => applyFilters(0)}>
              Apply filters
            </Button>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={title}
        columnLayoutTableId={columnLayoutTableId}
        columns={columns}
        rows={rows}
        rowKey={(row) =>
          String(
            row.RecordID ??
              row.Event_Record_ID ??
              `${formatWirelessCell(row.IMEINumber)}-${formatWirelessCell(row.EventTimestamp ?? row.date ?? row.PacketTimestamp)}`,
          )
        }
        loading={dataQuery.isLoading}
        loadingMessage="Loading..."
        error={dataQuery.isError ? "Could not load rows." : null}
        empty="No rows for these filters."
      />

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          {total} record{total === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={safePage <= 0 || dataQuery.isLoading}
            onClick={() => {
              const nextPage = safePage - 1;
              setPage(nextPage);
              setApplied((current) => ({ ...current, page: nextPage }));
            }}
          >
            Previous
          </Button>
          <span>
            Page {safePage + 1} of {pageCount}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={safePage >= pageCount - 1 || dataQuery.isLoading}
            onClick={() => {
              const nextPage = safePage + 1;
              setPage(nextPage);
              setApplied((current) => ({ ...current, page: nextPage }));
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
